import { InjectionToken, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { JSONValue } from '@growthbook/growthbook';
import { Store } from '@ngxs/store';
import { AuthSelectors } from 'src/app/store/auth/auth.selectors';
import { environment } from 'src/environments/environment';
import { AuthCompany, ReportType, WebAppTrackingMode } from 'src/models';
import { getDisableInvite, getMcpRolloutBatchFlag, isAlphaCustomer, isBeta, isBetaCustomer, isExecutive, isTimeDoctor, isTimeDoctorSales } from '../app/app.constants';
import { isBasicPricingPlan, UPSELL_SETUP_FLOW_FLAG } from '../constants/upsell-setup-flow.constants';
import { hasAnyExperimentalReportAccess, hasExperimentalReportAccess, reportDefs } from '../pages/experimental-reports/reports';
import {
  isSurfaceInviteButtonAnywhereB as isSurfaceInviteExperimentB,
  SURFACE_INVITE_BUTTON_ANYWHERE_FLAG
} from '../pages/invite/invite-page-experience';
import { APP_URL } from '../services/app.constants';
import { BrandingService } from '../services/branding.service';
import { DownloadInteractionService } from '../services/download-interaction.service';
import { GrowthBookFeature, GrowthBookService } from '../services/growthbook/service';
import { canUseAiConnections } from '../services/mcp/ai-connections-access.util';
import { NotificationVisitService } from '../services/notification-visit.service';
import type { AmpTrackComponentValue } from '../services/tracking/amp-track';
import { flatten } from '../util/array-helpers';
import { getVisitedPageState } from './localstorage-util';

const brandingService = new BrandingService(APP_URL.toString());

export type MenuCondition = (company: AuthCompany, injector: Injector) => boolean;

export interface MenuBadge {
  text?: string;
  icon?: string;
  condition?: MenuCondition;
  iconTooltip?: string;
  tooltip?: string;
  color?: string;
}

export interface MenuNotificationBubble {
  title: string | ((injector: Injector) => string);
  description: string | ((injector: Injector) => string);
  dismissButtonLabel?: string;
  position?: 'top' | 'bottom' | 'left' | 'right' | 'top-right';
  timeout?: number;
  id: string;
  condition?: MenuCondition;
  isTracking?: boolean;
  ctaButtonLabel?: string | ((injector: Injector) => string);
  ctaButtonAction?: (injector: Injector) => void;
  panelClass?: string;
  saveVisitByDB?: boolean;
}

export type MenuItem = {
  name: string;
  condition?: MenuCondition;
  routerLink: string;
  fragment?: string;
  badge?: MenuBadge | MenuBadge[];
  notificationBubble?: MenuNotificationBubble;
  ampTrack?: AmpTrackComponentValue;
} | {
  name: string;
  condition?: MenuCondition;
  href: string;
  badge?: MenuBadge | MenuBadge[];
  notificationBubble?: MenuNotificationBubble;
  ampTrack?: AmpTrackComponentValue;
};

export type MenuLabel = {
  name: string;
  condition?: MenuCondition;
};

export interface MenuParent {
  name: string;
  condition?: MenuCondition;
  children: (MenuItem | MenuLabel)[];
  badge?: MenuBadge | MenuBadge[];
  notificationBubble?: MenuNotificationBubble;
  group?: string;
  showHeaderBadgeIcon?: MenuCondition;
  ampTrack?: AmpTrackComponentValue;
}

export interface ReportGroup {
  name: string;
  key: string;
  reports: MenuItem[];
}

export interface MenuDef {
  items: (MenuItem | MenuParent)[];
  sideNavHeaderBadge?: MenuCondition;
}

export const MENU_DEF = new InjectionToken<MenuDef>('Menu Definition');

export const isWhiteLabel = () => !brandingService.isDefault;
export const disableDownload = () => brandingService.downloadDisabled();

const SOFT_CONDITION_TOKEN = new InjectionToken<boolean>('Soft condition token', {
  providedIn: 'root',
  factory: () => false,
});

export function createSoftConditionInjector(injector: Injector, value = true) {
  return Injector.create({
    name: 'Soft condition injector',
    parent: injector,
    providers: [
      { provide: SOFT_CONDITION_TOKEN, useValue: value },
    ],
  });
}

/**
 * Sometimes the requirements for a condition may not be initialized yet when evaluating a condition.
 * For example, feature flags may not be loaded yet when showing some menus.
 * It ok to not show the menu until feature flags are loaded completely.
 * However, if the user is already in that page,  our routing will kick the user out of the page in this case.
 * This is not desirable. Thus, we have a concept of soft conditions to solve this.
 * Soft conditions will always evaluate to true in soft condition contexts.
 * Route guards are an example of soft condition contexts.
 * @param condition The condition to evaluate if we are not in soft condition context
 * @returns Always true in soft condition context, result of the `condition` otherwise
 */
export function softCondition(condition: MenuCondition) {
  return ((company, injector) => {
    const isSoftCondition = injector.get(SOFT_CONDITION_TOKEN, false);
    return isSoftCondition || (!condition || condition(company, injector));
  }) as MenuCondition;
}

/**
 * Used for defining a feature-flag menu condition
 * @example
 * ```
 *   condition: hasFeatureFlag('billing-page', 'new')
 * ```
 */
export function hasFeatureFlag<T extends JSONValue = any>(flag: GrowthBookFeature, value: T, defaultValue?: T): MenuCondition {
  return (company, injector) => {
    const isSoftCondition = injector.get(SOFT_CONDITION_TOKEN, false);
    const gb = injector.get(GrowthBookService);

    // When GB is not initialized, return true in soft condition context
    if (!gb.initialized.value && isSoftCondition) return true;

    return !!gb.getFeatureValue<any>(flag, defaultValue) === value;
  };
}

/**
 * Used for defining a feature-flag menu condition with boolean values
 * @example
 * ```
 *   condition: isFeatureFlagOn('user-created-task')
 * ```
 */
export function isFeatureFlagOn(flag: GrowthBookFeature, defaultValue?: boolean): MenuCondition {
  return hasFeatureFlag(flag, true, defaultValue);
}

export function isRegularUser(company: AuthCompany) {
  return company.role === 'user';
}

export function isGuest(company: AuthCompany) {
  return company.role === 'guest';
}

export function hasMandatoryCall(company: AuthCompany) {
  const hasToBookCall = company?.companySettings?.custom?.['hasMandatoryCall'] ?? false;
  return company?.subscription?.status === 'trial' && hasToBookCall;
}

export function isManager(company: AuthCompany | undefined | null) {
  return ['manager', 'admin', 'owner'].includes(company?.role ?? '');
}

export function isAdmin(company: AuthCompany) {
  return ['admin', 'owner'].includes(company.role);
}

export function isOwner(company: AuthCompany) {
  return company.role === 'owner';
}

export function isTaskBased(company: AuthCompany) {
  return company.companySettings.tasksMode !== 'off' && company.companySettings.trackingMode !== 'silent';
}

export function isSilent(company: AuthCompany) {
  return company.companySettings.trackingMode === 'silent';
}

export function isInteractive(company: AuthCompany) {
  return !isSilent(company);
}

export function meetingInsightsGoogleCalendarEnabled(company: AuthCompany) {
  return company?.companySettings?.allowGoogleCalendarIntegration;
}

export function meetingInsightsGoogleCalendarAllowUsers(company: AuthCompany) {
  return company?.companySettings?.allowRegularUsersGCIntegration;
}

export function meetingInsightsMicrophoneAccess(company: AuthCompany) {
  return company?.companySettings?.microphoneAccess;
}

export function meetingInsightsTalkTimeEnabled(company: AuthCompany, injector: Injector): boolean {
  const gb = injector.get(GrowthBookService);
  const calendarIntegrationGbValue = gb.getFeatureValue<any>('meeting_insights_v2_google_calendar', null);
  return calendarIntegrationGbValue?.showTalkTimeForNonCalendarMeetings ?? false;
}

export function isTimedoctorCompany(company: AuthCompany) {
  return isTimeDoctor(company.id);
}

export function isTimedoctorSalesCompany(company: AuthCompany) {
  return isTimeDoctorSales(company.id);
}

export function isQueryExecutive(company: AuthCompany) {
  return isExecutive;
}

export function isCompanyBigForExecutive(company: AuthCompany) {
  return company?.userCount >= 20;
}

export function isAlphaCompany(company: AuthCompany) {
  return isAlphaCustomer(company.id) || environment.hmr;
}

export function isBetaCompany(company: AuthCompany) {
  return isBetaCustomer(company.id) || isBeta;
}

export function isNotBasicPlanWithNoBillingChangeDate(company: AuthCompany) {
  // we only need to apply pricing plan restriction if billingChangeDate exists
  // to exclude existing customers
  const applyRestrictions = !!company?.billingChangeDate;
  return !applyRestrictions || !company?.pricingPlan?.includes('basic');
}

export function isPricingPlanPremium(company: AuthCompany) {
  return company?.pricingPlan?.includes('premium');
}
export function connectivityEnabled(company: AuthCompany) {
  return company.companySettings.trackConnectivity;
}

export function isDevelopment() {
  return !environment.production;
}

export function guestHasAccessTo(report: ReportType) {
  return (company: AuthCompany) => company.userSettings.custom.allowedReports?.[report];
}

export function isManagerScreencastAccessAllowed(company: AuthCompany) {
  const disableManagersScreencastsToggle = disableManagersScreencasts(company);
  const whoCanAccessScreenshots = company?.companySettings?.whoCanAccessScreenshots || '';
  const managerIncludedInDropdown = whoCanAccessScreenshots.includes('manager');
  return !disableManagersScreencastsToggle || managerIncludedInDropdown;
}

export function isScreencastViewingAllowedSettingsForUsers(company: AuthCompany) {
  if (isAdmin(company)) {
    return true;
  }
  if (company.role === 'manager') {
    return isManagerScreencastAccessAllowed(company);
  }
  if (company.role === 'user' || company.role === 'guest') {
    return !disableRegularUsersScreencasts(company);
  }
  return false;
}

export function hasScreencasts(company: AuthCompany) {
  const ssUser = parseFloat(company?.userSettings.screenshots.toString());
  const isClientWithScreencastsReport = company.role === 'guest' && company?.userSettings?.custom?.allowedReports?.screencasts;
  const screencastsEnabledForUser = ssUser > 0 || company?.userSettings.videos === 'on' || isClientWithScreencastsReport;
  const screencastsEnabledForCompany = company?.companySettings.screencastsFeature !== false;
  const isScreencastViewingAllowedForUsers = isScreencastViewingAllowedSettingsForUsers(company);
  const canAdminAccessOthersSsWithoutOwnSs = isAdmin(company) && hasScreencastsAccessLevel(company);
  return screencastsEnabledForCompany
    && (company?.hasManagedScreencasts || screencastsEnabledForUser || canAdminAccessOthersSsWithoutOwnSs)
    && isScreencastViewingAllowedForUsers
    && (screencastsEnabledForUser || hasScreencastsAccessLevel(company));
}

export function hasScreencastsAccessLevel(company: AuthCompany) {
  const whoCanAccessScreenshots = company?.companySettings?.whoCanAccessScreenshots;

  if (company?.role === 'user' || company?.role === 'owner' || !whoCanAccessScreenshots) {
    return true;
  }

  return whoCanAccessScreenshots.includes(company.role);
}

function hasManagedApprovals(company: AuthCompany) {
  return isManager(company) && company.hasManagedApprovals;
}

export function regularUserNeedsApproval(company: AuthCompany, injector?: Injector): boolean {
  if (injector) {
    const gb = injector.get(GrowthBookService);
    if (gb.getFeatureValue('leave-tracking', false) && gb.getFeatureValue('leave-approval', false)) {
      return company?.role === 'user';
    }
  }
  return isNotBasicPlanWithNoBillingChangeDate(company) && company?.role === 'user' && company?.userSettings?.canEditTime === 'needApproval';
}

export function and(...conditions: MenuCondition[]): MenuCondition {
  return (company: AuthCompany, injector: Injector) => {
    return conditions.every(x => x == null || x(company, injector));
  };
}

export function or(...conditions: MenuCondition[]): MenuCondition {
  return (company: AuthCompany, injector: Injector) => {
    return conditions.some(x => x?.(company, injector));
  };
}

export function not(condition: MenuCondition): MenuCondition {
  return (company: AuthCompany, injector: Injector) => {
    return !condition(company, injector);
  };
}

export const schedulesEnabled = (company: AuthCompany) => company.companySettings.workScheduleFeature;

export const isUpsellSetupFlowFlagOn = isFeatureFlagOn(UPSELL_SETUP_FLOW_FLAG as GrowthBookFeature, false);

export const schedulesVisible = or(
  schedulesEnabled,
  isFeatureFlagOn('schedules:upsell'),
);

/** Nav entry: upsell on Basic bypasses plan gate (mirror payroll:upsell). */
export const schedulesNavVisible = or(
  isFeatureFlagOn('schedules:upsell'),
  and(isUpsellSetupFlowFlagOn, (company) => isBasicPricingPlan(company)),
  and(isNotBasicPlanWithNoBillingChangeDate, schedulesVisible),
);

export const aiConnectionsNavVisible = and(
  or(
    isFeatureFlagOn('mcp-enabled'),
    (company: AuthCompany, injector: Injector) => {
      const flag = getMcpRolloutBatchFlag(company.id);
      if (!flag) return false;
      return injector.get(GrowthBookService).getFeatureValue<boolean>(flag, false);
    },
  ),
  // Visible to Global Admin (login-as) too: the entry's presence tells support whether
  // the company has the feature. The page itself blocks any action in that session.
  (company: AuthCompany) => canUseAiConnections(company),
);

export const attendanceNavVisible = or(
  isFeatureFlagOn('attendance:upsell'),
  schedulesNavVisible,
);

export const hasEmailSubscription = (company: AuthCompany) => company?.userSettings.emailSubCount > 0;

export const payrollEnabled = and(
  isInteractive,
  (company: AuthCompany) =>
    company.companySettings.payrollFeature
    && (
      isOwner(company)
      || (company.userSettings.payrollAccess
        || (company.companySettings.whoCanAccessPayroll && company.companySettings.whoCanAccessPayroll.includes(company.role)))
    ),
);

export const disableReportAccess = (company: AuthCompany) => company?.userSettings?.disableReportsAccess;

export const billingAccess = and(
  not(isGuest),
  or(isOwner, (company: AuthCompany) => company.userSettings.billingAccess),
  () => !brandingService.billingDisabled(),
);

export const isTrialer = (company: AuthCompany) => {
  return company?.subscription?.status === 'trial';
};

export const isFiftyPlusSeatCompany = (company: AuthCompany) => (company?.userCount ?? 0) >= 50;

/**
 * Painted-door audience: trial or paying customers, i.e. not on the free/locked
 * plan. The exact subscription-status taxonomy for "paid" should be confirmed
 * with product before GA.
 */
export const isTrialOrPaidCustomer = (company: AuthCompany) => company?.subscription?.status !== 'free';

/**
 * Compliance Doctor painted-door visibility: gated behind the `compliance-doctor`
 * GrowthBook flag AND owners/admins/managers at 50+ seat, trial-or-paid companies.
 * The flag is additive (it does not replace the segment rules) and defaults to
 * false so the page stays hidden until the flag is explicitly turned on, rather
 * than being ambiguous during GrowthBook's pre-init window. Exported so the route
 * guard enforces the same combined gate.
 *
 * Deployment-based targeting (e.g. Vois exclusion) is handled in the GrowthBook
 * dashboard via the already-wired `deployment` attribute — no code change needed.
 */
export const complianceDoctorAudience: MenuCondition = and(
  isFeatureFlagOn('compliance-doctor', false),
  isManager,
  isFiftyPlusSeatCompany,
  isTrialOrPaidCustomer,
);

export const visitedPage = (page: string) => (getVisitedPageState(page) === 'true');
export const visitedNotifications = (notif: string) => {
  return (company, injector) => {
    const notifService = injector.get(NotificationVisitService);
    return notifService?.visitedNotification?.(notif) ?? false;
  };
};

export const shouldShowPremiumTrialBadge: MenuCondition = and(
  isTrialer,
  billingAccess,
  (company, injector) => !visitedNotifications('billingImp')(company, injector),
);

export const shouldShowSelfServeAddOnsBadge: MenuCondition = and(
  isFeatureFlagOn('self-serve-addon-awareness', false),
  (company, injector) => !visitedNotifications('selfServeAddonAwareness')(company, injector),
  billingAccess,
);

export const shouldShowNativeIntegrationsBadge: MenuCondition = and(
  isInteractive,
  isFeatureFlagOn('td-marketplace', false),
  isManager,
  () => !visitedPage('integrations'),
  (company) => {
    const custom = company?.companySettings?.custom || {};
    return !(custom.integrationsPageSeen ?? custom.nativeIntegrationsVisited);
  },
);

export const shouldShowBillingEnhancementBadge: MenuCondition = and(
  isFeatureFlagOn('new-billing:enhancement-awareness', false),
  billingAccess,
);

export const webAppTrackingIs = (value: WebAppTrackingMode) => (x => x.companySettings?.webAndAppTracking === value) as MenuCondition;
export const hasWebAppTracking = not(webAppTrackingIs(WebAppTrackingMode.Off));

export const editTimeEnabled = and(not(isGuest), ((company: AuthCompany) => company.userSettings.canEditTime !== 'no'));

export const productivityEnabledForManager = and(x => x.companySettings?.allowManagerTagCategories, hasWebAppTracking);
export const projectsEnabledForManager: MenuCondition = x => x.companySettings?.allowManagerProjectsTasks;
export const inviteEnabledForManager: MenuCondition = x => x.companySettings?.allowManagerInviteUsers;

/** Per-user invite permission (STAFF-145775) — the grant and the flag must both be on. */
export const hasInviteUsersPermission: MenuCondition = and(
  x => !!x?.userSettings?.allowInviteUsers,
  isFeatureFlagOn('user-onboarding-permissions', false),
);
export const isManualProvisioningEnabled: MenuCondition = x => !x.companySettings?.disableManualUserProvisioning;

/** B hides the dedicated Invite nav item; the flag and variant check live in invite-page-experience. */
export const isSurfaceInviteButtonAnywhereB: MenuCondition = (_company, injector) => {
  const gb = injector.get(GrowthBookService);
  return isSurfaceInviteExperimentB(gb.getFeatureValue(SURFACE_INVITE_BUTTON_ANYWHERE_FLAG));
};

export const inviteNavVisible: MenuCondition = and(
  or(isAdmin, and(isManager, inviteEnabledForManager), hasInviteUsersPermission),
  isManualProvisioningEnabled,
  isInteractive,
  () => !getDisableInvite(),
  // Soft so /invite stays reachable from other CTAs; only the dedicated nav item hides.
  softCondition(not(isSurfaceInviteButtonAnywhereB)),
);

export const allowUsersActivitySummaryReport = (company: AuthCompany) => company.companySettings?.allowUsersActivitySummaryReport;
export const disableRegularUsersScreencasts = (
  company: AuthCompany | undefined | null,
) => company?.companySettings?.disableRegularUsersScreencasts ?? false;

export const disableManagersScreencasts = (
  company: AuthCompany | undefined | null,
) => company?.companySettings?.disableManagersScreencasts ?? false;

export const excludeManagerFromFilter = (company: AuthCompany) => company?.role === 'manager' && company?.companySettings?.disableManagersScreencasts;

const isBasicPlan: MenuCondition = x => x?.pricingPlan?.includes('basic');

/** Std/Premium approvals access requires interactive tracking, reports access, and non-Basic plan. */
export function approvalsHardPrerequisitesMet(company: AuthCompany | undefined | null): boolean {
  if (!company) {
    return false;
  }
  return isInteractive(company)
    && !disableReportAccess(company)
    && !company.pricingPlan?.includes('basic');
}

export const breaksNavVisible = or(
  isFeatureFlagOn('breaks:upsell'),
  and(not(isBasicPlan), isInteractive, isFeatureFlagOn('breaks', false)),
);

export const approvalsNavVisible = or(
  isFeatureFlagOn('approvals:upsell'),
  and(
    (company: AuthCompany) => approvalsHardPrerequisitesMet(company),
    or(regularUserNeedsApproval, hasManagedApprovals, isManager),
  ),
);

export const productivityRatingsNavVisible = or(
  isFeatureFlagOn('productivity-ratings:upsell'),
  and(hasWebAppTracking, isFeatureFlagOn('productivity-ratings:enabled')),
);

export const internetConnectivityNavVisible = or(
  isFeatureFlagOn('internet-connectivity:upsell'),
  and(connectivityEnabled, isPricingPlanPremium),
);

const isStandardPlan: MenuCondition = x => x?.pricingPlan?.toLowerCase().includes('standard');
const isStandardOrBasicPlan: MenuCondition = or(isStandardPlan, isBasicPlan);
const isPremiumUnderTwentyUsers: MenuCondition = (company: AuthCompany) =>
  isPricingPlanPremium(company) && (company?.userCount ?? 0) < 20;
const shouldShowExecutiveUpgradeBadge: MenuCondition = and(
  or(isStandardOrBasicPlan, isPremiumUnderTwentyUsers),
  isFeatureFlagOn('executive-dashboard-upsell-page'),
  () => !visitedPage('dashboard-executive'),
);
export const isAddonsPageEnabled: MenuCondition = and(
  isFeatureFlagOn('addons-page'),
  billingAccess,
  or(isBasicPlan, isStandardPlan),
);

export const hasNotificationsAccess: MenuCondition = or(not(isBasicPlan), isFeatureFlagOn('email-notifications-upsell'));

export const hasBenchmarksAddOn: MenuCondition = (company: AuthCompany) =>
  company?.subscription?.addOns?.some(addon => addon?.includes('benchmarks_add-on')) ?? false;

export const experimentalReportMenuItems: MenuItem[] = reportDefs.map(reportDef => ({
  name: `experimental.reports.${reportDef.id}`,
  condition: hasExperimentalReportAccess(reportDef),
  routerLink: reportDef.routerLink ?? '/experimental-reports',
  fragment: reportDef.id,
  badge: reportDef.badge,
}));

/**
 * Single source of truth for all report definitions
 * Maps report IDs to their full menu item configuration
 */
const reportDefinitions = {
  // Productivity & Focus reports
  activitySummary: {
    name: 'reports.activitySummary',
    routerLink: '/activity-summary',
    ampTrack: 'nav-activity-summary' as const,
    condition: or(
      isFeatureFlagOn('activity-summary:upsell'),
      and(hasWebAppTracking, or(isManager, allowUsersActivitySummaryReport)),
      isFeatureFlagOn('activity-summary:as-addon'),
    ),
    badge: { text: 'common.new', condition: and(isFeatureFlagOn('activity-summary:upsell'), () => !visitedPage('activity-summary')) },
  },
  // Points at /signals (the new AI Signals page, also opened by the TD logo). [AA-138]
  productivityInsights: { name: 'insights.menuLabel', routerLink: '/signals', ampTrack: 'nav-insights' as const, condition: and(isManager, isFeatureFlagOn('ask-ai-new-signals-page')), badge: { text: 'common.beta', color: 'beta' } },
  hoursTracked: { name: 'reports.hoursTracked', routerLink: '/hours-tracked', ampTrack: 'nav-hours-tracked' as const },
  projectsAndTasks: { name: 'reports.projectsAndTasks', routerLink: '/projects-report', ampTrack: 'nav-projects-tasks-report' as const, condition: isTaskBased },
  timeline: { name: 'reports.timeline', routerLink: '/timeline', ampTrack: 'nav-timeline' as const },
  webAndAppUsage: { name: 'reports.webAndAppUsage', routerLink: '/web-app-usage', ampTrack: 'nav-web-app-usage' as const, condition: or(hasWebAppTracking, isFeatureFlagOn('web-and-app-usage:upsell')), badge: { text: 'common.new', condition: and(isFeatureFlagOn('web-and-app-usage:upsell'), () => !visitedPage('web-app-usage')) } },

  // Performance & Collaboration reports
  aiAdoption: { name: 'reports.aiAdoption', routerLink: '/ai-impact', ampTrack: 'nav-ai-impact' as const, condition: and(isManager, isFeatureFlagOn('ai-adoption-report-v2')), badge: { text: 'common.beta', color: 'beta' } },
  attendance: { name: 'reports.attendance', routerLink: '/attendance', ampTrack: 'nav-attendance' as const, condition: attendanceNavVisible, badge: { text: 'common.new', condition: and(isFeatureFlagOn('attendance:upsell'), () => !visitedPage('attendance')) } },
  benchmarking: { name: 'reports.benchmarking', routerLink: '/benchmarks', ampTrack: 'nav-benchmarks-ai' as const, condition: and(isManager, isFeatureFlagOn('benchmarking-report')), badge: { text: 'common.improved', condition: and(isFeatureFlagOn('benchmarking-report:meeting-time-widget'), () => !visitedPage('benchmarks')) } },
  meetingInsights: { name: 'reports.meetingInsights', routerLink: '/meeting-insights', ampTrack: 'nav-meeting-insights' as const, condition: or(isFeatureFlagOn('meeting-insights-widgets'), isFeatureFlagOn('meeting-insights-in-dashboard-cta')), badge: { text: 'common.improved', condition: and(meetingInsightsTalkTimeEnabled, () => !visitedPage('meeting-insights')) } },
  unusualActivity: { name: 'reports.unusualActivity', routerLink: '/unusual-activity', ampTrack: 'nav-unusual-activity' as const, condition: and(isManager, or(isFeatureFlagOn('unusual-activity-report'), isFeatureFlagOn('unusual-activity-upsell'))) },

  // Operations & Cost Management reports
  complianceDoctor: { name: 'reports.complianceDoctor', routerLink: '/compliance-doctor', badge: { text: 'common.new' }, ampTrack: 'nav-compliance-doctor', condition: complianceDoctorAudience },
  csv: { name: 'reports.csv', routerLink: '/csv', ampTrack: 'nav-custom-export' as const, condition: isManager },
  connectivity: {
    name: 'reports.connectivity',
    routerLink: '/internet-report',
    ampTrack: 'nav-internet-connectivity' as const,
    condition: internetConnectivityNavVisible,
    badge: { text: 'common.new', condition: and(isFeatureFlagOn('internet-connectivity:upsell'), () => !visitedPage('internet-connectivity')) },
  },
  officeVsRemoteV1: { name: 'reports.officeVsRemote', routerLink: '/office-vs-remote-v1', ampTrack: 'nav-office-vs-remote' as const, condition: isFeatureFlagOn('office-vs-remote-report'), badge: { text: 'common.new', condition: () => !visitedPage('office-vs-remote-v1') } },
  officeVsRemoteV2: { name: 'reports.officeVsRemote', routerLink: '/office-vs-remote', ampTrack: 'nav-office-vs-remote' as const, condition: or(isFeatureFlagOn('office-vs-remote-report-v2'), isFeatureFlagOn('office-vs-remote:upsell')) },
  softwareCostInsights: { name: 'reports.softwareCostInsights', routerLink: '/software-cost-insights', ampTrack: 'nav-software-cost-insights' as const, condition: and(isAdmin, hasWebAppTracking, isFeatureFlagOn('software-cost-insights')) },

  // Experimental reports
  workforceIntelligence: { name: 'reports.workforceIntelligence', routerLink: '/workforce-intelligence', condition: and(isManager, isFeatureFlagOn('workforce-intelligence-report')), badge: { text: 'common.beta', color: 'beta' } },
  wrenKitchens: { name: 'reports.wrenKitchensUrlSearch', routerLink: '/wren-kitchens-app-url-search', condition: isFeatureFlagOn('wren-kitchens-url-search-report'), badge: { text: 'common.new', condition: () => !visitedPage('wren-kitchens-url-search') } },
  hours24Team: { name: 'reports.24HoursTeamAuditLog', routerLink: '/24hours-team-audit-log', condition: isFeatureFlagOn('24hours-team-audit-log'), badge: { text: 'common.new', condition: () => !visitedPage('24hours-team-audit-log') } },
  celcoinAudit: { name: 'reports.celcoinAuditLog', routerLink: '/celcoin-audit-log', condition: isFeatureFlagOn('celcoin-audit-log'), badge: { text: 'common.new', condition: () => !visitedPage('celcoin-audit-log') } },
  chinaBankRemoteData: { name: 'reports.chinaBankRemoteDataReport', routerLink: '/chinabank-remotedata-report', condition: isFeatureFlagOn('chinabank-remote-data-report'), badge: { text: 'common.new', condition: () => !visitedPage('chinabank-remotedata-report') } },
  retentionData: { name: 'reports.retentionDataReport', routerLink: '/retention-data-report', condition: isFeatureFlagOn('ring-ring-marketing-retention-data-report'), badge: { text: 'common.new', condition: () => !visitedPage('retention-data-report') } },
  clarkeTelecomRetentionData: { name: 'reports.clarkeTelecomRetentionDataReport', routerLink: '/clarke-telecom-retention-data-report', condition: isFeatureFlagOn('clarke-telecom-retention-data-report'), badge: { text: 'common.new', condition: () => !visitedPage('clarke-telecom-retention-data-report') } },
  lavaAutomationPto: { name: 'reports.lavaAutomationPtoReport', routerLink: '/lavaautomation-pto-report', condition: isFeatureFlagOn('lava-automation-retention-data-report'), badge: { text: 'common.new', condition: () => !visitedPage('lavaautomation-pto-report') } },

  // Placeholder for experimental menu item
  experimentalLabel: { name: 'reports.experimental', condition: hasAnyExperimentalReportAccess, badge: { text: 'common.new' } },
} as const;

/**
 * Grouped reports for A/B test variants
 * Organizes reports into logical categories using stable references from reportDefinitions
 */
export const groupedReports: ReportGroup[] = [
  {
    name: 'reports.groups.productivityAndFocus',
    key: 'PRODUCTIVITY_FOCUS',
    reports: [
      reportDefinitions.activitySummary,
      reportDefinitions.productivityInsights,
      reportDefinitions.hoursTracked,
      reportDefinitions.projectsAndTasks,
      reportDefinitions.timeline,
      reportDefinitions.webAndAppUsage,
    ],
  },
  {
    name: 'reports.groups.performanceAndCollaboration',
    key: 'PERFORMANCE_COLLABORATION',
    reports: [
      reportDefinitions.aiAdoption,
      reportDefinitions.attendance,
      reportDefinitions.benchmarking,
      reportDefinitions.meetingInsights,
      reportDefinitions.unusualActivity,
    ],
  },
  {
    name: 'reports.groups.operationsAndCostManagement',
    key: 'OPERATIONS_COST',
    reports: [
      reportDefinitions.complianceDoctor,
      reportDefinitions.csv,
      reportDefinitions.connectivity,
      reportDefinitions.officeVsRemoteV1,
      reportDefinitions.officeVsRemoteV2,
      reportDefinitions.softwareCostInsights,
    ],
  },
  {
    name: 'reports.groups.experimentalReports',
    key: 'EXPERIMENTAL',
    reports: [
      reportDefinitions.workforceIntelligence,
      reportDefinitions.wrenKitchens,
      reportDefinitions.hours24Team,
      reportDefinitions.celcoinAudit,
      reportDefinitions.chinaBankRemoteData,
      reportDefinitions.retentionData,
      reportDefinitions.clarkeTelecomRetentionData,
      reportDefinitions.lavaAutomationPto,
      ...experimentalReportMenuItems,
    ],
  },
];

export const managerSettings: (MenuItem | MenuParent)[] = [
  {
    name: 'header.settings',
    ampTrack: 'nav-settings-dropdown' as const,
    condition: and(isManager, not(isAdmin), or(
      hasNotificationsAccess,
      productivityEnabledForManager,
      projectsEnabledForManager,
      billingAccess,
      schedulesVisible,
      aiConnectionsNavVisible,
    )),
    children: [
      { name: 'header.settingsBilling', routerLink: '/billing', ampTrack: 'nav-billing' as const, condition: billingAccess },
      {
        name: 'header.settingsAiConnections',
        routerLink: '/ai-connections',
        ampTrack: 'nav-ai-connections' as const,
        condition: aiConnectionsNavVisible,
        badge: [
          { text: 'common.new', condition: () => !visitedPage('ai-connections') },
        ],
      },
      { name: 'header.settingsTasks', routerLink: '/projects-and-tasks', ampTrack: 'nav-project-task-settings' as const, condition: and(isTaskBased, projectsEnabledForManager) },
      {
        name: 'header.settingsNotifications', routerLink: '/notifications', ampTrack: 'nav-email-notifications' as const, condition: hasNotificationsAccess, badge: [
          { text: 'common.new', condition: and(isFeatureFlagOn('email-notifications-upsell'), () => !visitedPage('notifications')) },
        ],
      },
      {
        name: 'header.settingsProductivity', routerLink: '/productivity-ratings', ampTrack: 'nav-productivity-ratings' as const, condition: productivityRatingsNavVisible,
        badge: [
          { text: 'common.new', condition: and(isFeatureFlagOn('productivity-ratings:upsell'), () => !visitedPage('productivity-ratings')) },
          { text: 'common.improved', condition: and(productivityEnabledForManager, isFeatureFlagOn('menu-productivity-ratings-improved-badge')) },
          { icon: 'announcement', iconTooltip: 'errors.accessContactAdmin', condition: not(productivityEnabledForManager) },
        ],
      },
      {
        name: 'header.settingsSchedules',
        routerLink: '/schedules',
        ampTrack: 'nav-schedules' as const,
        condition: schedulesNavVisible,
        badge: { text: 'common.new', condition: and(isFeatureFlagOn('schedules:upsell'), () => !visitedPage('schedules')) },
      },
    ],
    showHeaderBadgeIcon: shouldShowNativeIntegrationsBadge,
  },
];

export const userSettings: (MenuItem | MenuParent)[] = [
  {
    name: 'header.settings',
    ampTrack: 'nav-settings-dropdown' as const,
    condition: and(isRegularUser, or(billingAccess, schedulesVisible)),
    children: [
      { name: 'header.settingsBilling', routerLink: '/billing', ampTrack: 'nav-billing' as const, condition: billingAccess },
      {
        name: 'header.settingsSchedules',
        routerLink: '/schedules',
        ampTrack: 'nav-schedules' as const,
        condition: schedulesNavVisible,
        badge: { text: 'common.new', condition: and(isFeatureFlagOn('schedules:upsell'), () => !visitedPage('schedules')) },
      },
      {
        name: 'header.settingsNotifications', routerLink: '/notifications', ampTrack: 'nav-email-notifications' as const,
        condition: and(isFeatureFlagOn('email-recipients'), hasEmailSubscription, isRegularUser, hasNotificationsAccess, or(billingAccess, schedulesVisible)),
      },
    ],
  },
];

export const guestReports: MenuItem[] = [
  {
    name: 'reports.aiAdoption',
    routerLink: '/ai-impact',
    ampTrack: 'nav-ai-impact' as const,
    condition: and(isGuest, guestHasAccessTo('aiAdoption'), isFeatureFlagOn('ai-adoption-report-v2')),
    badge: { text: 'common.beta', color: 'beta' },
  },
  {
    name: 'reports.projectsAndTasks',
    routerLink: '/projects-report',
    ampTrack: 'nav-projects-tasks-report' as const,
    condition: and(isTaskBased, isGuest, guestHasAccessTo('projectsAndTasks')),
  },
  {
    name: 'reports.screencasts',
    routerLink: '/screencasts',
    ampTrack: 'nav-screencasts' as const,
    condition: and(hasScreencasts, isGuest, guestHasAccessTo('screencasts')),
  },
  {
    name: 'reports.activitySummary',
    routerLink: '/activity-summary',
    ampTrack: 'nav-activity-summary' as const,
    condition: and(isGuest, guestHasAccessTo('activitySummary')),
  },
  {
    name: 'reports.hoursTracked',
    routerLink: '/hours-tracked',
    ampTrack: 'nav-hours-tracked' as const,
    condition: and(isGuest, guestHasAccessTo('hoursTracked')),
  },
  {
    name: 'reports.webAndAppUsage',
    routerLink: '/web-app-usage',
    ampTrack: 'nav-web-app-usage' as const,
    condition: and(isGuest, guestHasAccessTo('webAndAppUsage'), hasWebAppTracking),
  },
  {
    name: 'reports.connectivity',
    routerLink: '/internet-report',
    ampTrack: 'nav-internet-connectivity' as const,
    condition: and(isGuest, guestHasAccessTo('connectivity')),
  },
  {
    name: 'reports.timeline',
    routerLink: '/timeline',
    ampTrack: 'nav-timeline' as const,
    condition: and(isGuest, guestHasAccessTo('timeline')),
  },
  {
    name: 'reports.attendance',
    routerLink: '/attendance',
    ampTrack: 'nav-attendance' as const,
    condition: and(isGuest, guestHasAccessTo('attendance')),
  },
];

/**
 * Determines if the download badge should be shown based on user interaction and state
 */
export const shouldShowDownloadBadge: MenuCondition = and(
  not(isGuest),
  not(disableDownload),
  (company, injector) => {
    // Early return if split test is disabled
    const gb = injector.get(GrowthBookService);
    const experimentResult = gb.getFeatureValue<any>('exp-activate-v2');
    if (experimentResult?.variationid !== 'B') {
      return false;
    }

    // Extract services and state once
    const downloadInteractionService = injector.get(DownloadInteractionService);
    const store = injector.get(Store);
    const router = injector.get(Router);

    const interactedWithNotification = downloadInteractionService.interactedWithNotification();
    if (interactedWithNotification) {
      return false;
    }

    // Get company settings
    const browserExtensionEnabled = company?.companySettings?.custom?.browserExtensionEnabled;
    const isAutomaticTracking = company?.companySettings?.trackingMode === 'silent';
    // Get download interaction states
    const hasDownloadDialogInteracted = downloadInteractionService.getDownloadDialogInteracted();
    const hasDownloadInteraction = downloadInteractionService.hasInteractedWithDownloadAction();
    const interactedWithDesktopApp = downloadInteractionService.interactedWithDesktopApp();
    const interactedWithBrowserExtension = downloadInteractionService.interactedWithBrowserExtension();

    // Get application state
    const hasTrackedTime = Boolean(store.selectSnapshot(AuthSelectors.hasTrackedTime));
    const currentPath = router.url;

    // Early return for desktop app users when browser extension is disabled
    if (!browserExtensionEnabled && interactedWithDesktopApp && !interactedWithBrowserExtension) {
      return false;
    }

    // Early return on browser extension callout for automatic tracking type
    if (hasDownloadDialogInteracted && interactedWithDesktopApp && isAutomaticTracking) {
      return false;
    }

    // Check if badge should be shown
    return hasDownloadDialogInteracted && !hasTrackedTime && !hasDownloadInteraction && currentPath !== '/downloads';
  },
);



const menuDef: MenuDef = {
  items: [
    {
      name: 'header.dashboard',
      routerLink: '/dashboard-individual',
      ampTrack: 'nav-user-dashboard' as const,
      condition: and(isRegularUser, not(disableReportAccess)),
    },
    {
      name: 'header.dashboard',
      ampTrack: 'nav-dashboard-dropdown' as const,
      children: [
        {
          name: 'header.executive', routerLink: '/dashboard-executive', ampTrack: 'nav-executive-dashboard' as const, condition: and(
            isAdmin,
            or(isFeatureFlagOn('executive-dashboard'), isFeatureFlagOn('executive-dashboard-upsell-page')),
            or(isCompanyBigForExecutive, isFeatureFlagOn('executive-dashboard-upsell-page')),
          ), badge: { text: 'common.upgrade', condition: shouldShowExecutiveUpgradeBadge },
        },
        { name: 'header.team', routerLink: '/dashboard', ampTrack: 'nav-team-dashboard' as const },
        { name: 'header.user', routerLink: '/dashboard-individual', ampTrack: 'nav-user-dashboard' as const },
      ],
      condition: and(isManager, not(disableReportAccess)),
    },
    {
      name: 'header.reports',
      ampTrack: 'nav-reports-dropdown' as const,
      children: [
        reportDefinitions.aiAdoption,
        reportDefinitions.officeVsRemoteV1,
        reportDefinitions.officeVsRemoteV2,
        reportDefinitions.benchmarking,
        reportDefinitions.workforceIntelligence,
        reportDefinitions.softwareCostInsights,
        reportDefinitions.meetingInsights,
        reportDefinitions.activitySummary,
        reportDefinitions.productivityInsights,
        reportDefinitions.unusualActivity,
        reportDefinitions.attendance,
        reportDefinitions.hoursTracked,
        reportDefinitions.projectsAndTasks,
        reportDefinitions.timeline,
        reportDefinitions.webAndAppUsage,
        reportDefinitions.connectivity,
        reportDefinitions.complianceDoctor,
        reportDefinitions.csv,
        reportDefinitions.experimentalLabel,
        reportDefinitions.wrenKitchens,
        reportDefinitions.hours24Team,
        reportDefinitions.celcoinAudit,
        reportDefinitions.chinaBankRemoteData,
        reportDefinitions.retentionData,
        reportDefinitions.clarkeTelecomRetentionData,
        reportDefinitions.lavaAutomationPto,
        ...experimentalReportMenuItems,
      ],
      condition: and(not(isGuest), not(disableReportAccess)),
    },
    {
      name: 'reports.screencasts',
      routerLink: '/screencasts',
      ampTrack: 'nav-screencasts' as const,
      condition: and(hasScreencasts, not(isGuest), not(disableReportAccess), or(isAdmin, not(disableManagersScreencasts),
        not(disableRegularUsersScreencasts))),
    },
    ...guestReports,
    {
      name: 'header.editTime',
      routerLink: '/edit-time',
      ampTrack: 'nav-edit-time' as const,
      condition: and(editTimeEnabled, not(disableReportAccess)),
    },
    {
      name: 'header.approvals',
      routerLink: '/approvals',
      ampTrack: 'nav-approvals' as const,
      condition: approvalsNavVisible,
    },
    ...managerSettings,
    ...userSettings,
    {
      name: 'header.settings',
      ampTrack: 'nav-settings-dropdown' as const,
      condition: and(isAdmin),
      children: [
        {
          name: 'header.settingsBilling', routerLink: '/billing', ampTrack: 'nav-billing' as const, condition: billingAccess,
          badge: [
            { text: 'sidebar.widget.premiumTrial', condition: and(shouldShowPremiumTrialBadge, not(shouldShowBillingEnhancementBadge)) },
            { text: 'common.improved', condition: or(and(isAddonsPageEnabled, () => !visitedPage('addons')), shouldShowBillingEnhancementBadge, shouldShowSelfServeAddOnsBadge) },
          ],
        },
        {
          name: 'header.settingsAiConnections',
          routerLink: '/ai-connections',
          ampTrack: 'nav-ai-connections' as const,
          condition: aiConnectionsNavVisible,
          badge: [
            { text: 'common.new', condition: () => !visitedPage('ai-connections') },
          ],
        },
        { name: 'header.settingsTasks', routerLink: '/projects-and-tasks', ampTrack: 'nav-project-task-settings' as const, condition: isTaskBased },
        {
          name: 'header.settingsProductivity',
          routerLink: '/productivity-ratings',
          ampTrack: 'nav-productivity-ratings' as const,
          condition: productivityRatingsNavVisible,
          badge: [
            { text: 'common.new', condition: and(isFeatureFlagOn('productivity-ratings:upsell'), () => !visitedPage('productivity-ratings')) },
            { text: 'common.improved', condition: isFeatureFlagOn('menu-productivity-ratings-improved-badge') },
          ],
        },
        { name: 'header.settingsUsers', routerLink: `/manage-users`, ampTrack: 'nav-users' as const },
        { name: 'header.settingsGroups', routerLink: '/manage-user-groups', ampTrack: 'nav-groups' as const },
        {
          name: 'header.settingsNotifications', routerLink: '/notifications', ampTrack: 'nav-email-notifications' as const, condition: and(hasNotificationsAccess, not(disableReportAccess)), badge: [
            { text: 'common.new', condition: and(isFeatureFlagOn('email-notifications-upsell'), () => !visitedPage('notifications')) },
          ],
        },
        {
          name: 'header.settingsIntegrations',
          routerLink: '/integrations',
          ampTrack: 'nav-integrations' as const,
          condition: isInteractive,
          badge: { text: 'common.improved', condition: shouldShowNativeIntegrationsBadge },
        },
        { name: 'header.settingsCompany', routerLink: '/company/edit', ampTrack: 'nav-company-settings' as const },
        {
          name: 'header.settingsSchedules',
          routerLink: '/schedules',
          ampTrack: 'nav-schedules' as const,
          condition: schedulesNavVisible,
          badge: { text: 'common.new', condition: and(isFeatureFlagOn('schedules:upsell'), () => !visitedPage('schedules')) },
        },
        {
          name: 'header.breaks',
          routerLink: '/breaks',
          ampTrack: 'nav-breaks' as const,
          condition: breaksNavVisible,
          badge: { text: 'common.new', condition: and(isFeatureFlagOn('breaks:upsell'), () => !visitedPage('breaks')) },
        },
      ],
      showHeaderBadgeIcon: or(
        shouldShowPremiumTrialBadge,
        shouldShowBillingEnhancementBadge,
        shouldShowSelfServeAddOnsBadge,
        shouldShowNativeIntegrationsBadge,
        and(isAddonsPageEnabled, () => !visitedPage('addons')),
      ),
    },
    { name: 'header.settingsNotifications', routerLink: '/notifications', condition: and(isFeatureFlagOn('email-recipients'), hasEmailSubscription, isRegularUser, hasNotificationsAccess, not(billingAccess), not(schedulesEnabled)) },
    {
      name: 'header.payroll', routerLink: '/payroll', ampTrack: 'nav-payroll' as const, condition: or(and(isFeatureFlagOn('payroll:upsell'), isManager), and(payrollEnabled, not(isGuest), not(disableReportAccess))),
      badge: [
        { text: 'common.improved', condition: and(not(isFeatureFlagOn('payroll:upsell')), isFeatureFlagOn('integration-deel'), () => !visitedPage('payroll')) },
        { text: 'common.new', condition: and(isFeatureFlagOn('payroll:upsell'), () => !visitedPage('payroll')) },
      ],
    },
    { name: 'header.invite', routerLink: '/invite', ampTrack: 'nav-invite' as const, condition: inviteNavVisible },
    { name: 'header.addManagers', routerLink: '/add-managers', ampTrack: 'nav-add-managers' as const, condition: and(isAdmin, isSilent, isManualProvisioningEnabled) },
    {
      name: 'header.download', routerLink: '/downloads', ampTrack: 'nav-download' as const, condition: and(not(isGuest), or(and(not(disableDownload), not(isFeatureFlagOn('disable-download', false))), isOwner)),
      notificationBubble: createDownloadNotificationBubble(),
      badge: {
        text: '',
        condition: shouldShowDownloadBadge,
      },
    },
  ],
  sideNavHeaderBadge: or(
    shouldShowPremiumTrialBadge,
    and(isAddonsPageEnabled, () => !visitedPage('addons'), () => !visitedPage('company-settings')),
  ),
};


// Require condition of both parent and children (and operator) in nested scenario
const flatMenu = flatten(menuDef.items.map(x => ('children' in x) ?
  x.children.map(c => ({ ...c, condition: and(c.condition, x.condition) })) : x));

// Require condition of atleast one of alternate routes (or operator) in parallel scenario
const conditionMap = new Map<string, MenuCondition>();
for (const cnd of flatMenu) {
  if ('routerLink' in cnd) {
    const existingCondition = conditionMap.get(cnd.routerLink);
    const newCondition = or(existingCondition, cnd.condition);
    conditionMap.set(cnd.routerLink, newCondition);
  }
}

export function isInMenu(url: string) {
  return !!conditionMap.get(url);
}

/**
 * Determines if a page is accessible by checking the conitions in the menu definitions
 * @param company Company retrieved from auth request
 * @param injector Angular injector service
 * @param url The url starting with `/`
 */
export function isMenuAllowed(company: AuthCompany, injector: Injector, url: string) {
  const condition = conditionMap.get(url);
  if (!condition) return false;
  return !!condition(company, createSoftConditionInjector(injector));
}

export const menuProvider = {
  provide: MENU_DEF,
  useFactory: () => menuDef,
};

/**
 * Creates a notification bubble for the download menu item with dynamic content
 * based on the user's download status
 */
function createDownloadNotificationBubble(): MenuNotificationBubble {
  // Helper function to get download status once and reuse it
  const getDownloadStatus = (injector: Injector) => {
    const downloadInteractionService = injector.get(DownloadInteractionService);
    return {
      interactedWithBrowserExtension: downloadInteractionService.interactedWithBrowserExtension(),
      interactedWithDesktopApp: downloadInteractionService.interactedWithDesktopApp(),
    };
  };

  // Cache services to avoid repeated injection
  const getDownloadInteractionServices = (injector: Injector) => {
    return {
      router: injector.get(Router),
      downloadInteractionService: injector.get(DownloadInteractionService),
    };
  };

  return {
    title: (injector: Injector) => {
      const { interactedWithBrowserExtension, interactedWithDesktopApp } = getDownloadStatus(injector);
      return interactedWithDesktopApp && !interactedWithBrowserExtension
        ? 'activate.downloadNotificationBubble.titleBoostProductivity'
        : 'activate.downloadNotificationBubble.titleGetMyInsights';
    },
    description: (injector: Injector) => {
      const { interactedWithBrowserExtension, interactedWithDesktopApp } = getDownloadStatus(injector);
      return interactedWithDesktopApp && !interactedWithBrowserExtension
        ? 'activate.downloadNotificationBubble.descriptionBoostProductivity'
        : 'activate.downloadNotificationBubble.descriptionGetMyInsights';
    },
    ctaButtonLabel: (injector: Injector) => {
      const { interactedWithBrowserExtension, interactedWithDesktopApp } = getDownloadStatus(injector);
      return interactedWithDesktopApp && !interactedWithBrowserExtension
        ? 'activate.downloadNotificationBubble.ctaButtonLabelBoostProductivity'
        : 'activate.downloadNotificationBubble.ctaButtonLabelGetMyInsights';
    },
    ctaButtonAction: (injector: Injector) => {
      const { router, downloadInteractionService } = getDownloadInteractionServices(injector);
      const interactedWithDesktopApp = downloadInteractionService.interactedWithDesktopApp();
      const interactedWithBrowserExtension = downloadInteractionService.interactedWithBrowserExtension();
      if (interactedWithDesktopApp && !interactedWithBrowserExtension) {
        downloadInteractionService.setTooltipInteraction('interacted-browser-extension-tooltip');
      } else {
        downloadInteractionService.setTooltipInteraction('interacted-desktop-app-tooltip');
      }
      router.navigate(['/downloads']);
    },
    dismissButtonLabel: 'common.dismiss',
    position: 'top-right',
    timeout: 1000,
    id: 'download-notification-bubble',
    condition: shouldShowDownloadBadge,
    isTracking: true,
    saveVisitByDB: true,
  };
}

/**
 * Helper to check if a report group has any visible reports
 */
export function hasVisibleReportsInGroup(group: ReportGroup): MenuCondition {
  return (company, injector) => {
    return group.reports.some(report =>
      !report.condition || report.condition(company, injector),
    );
  };
}

/**
 * Helper to get filtered reports for a group based on conditions
 */
export function getVisibleReportsForGroup(group: ReportGroup, company: AuthCompany, injector: Injector): MenuItem[] {
  return group.reports.filter(report =>
    !report.condition || report.condition(company, injector),
  );
}

/**
 * Per-component memo for {@link getVisibleReportsForGroup}.
 *
 * Visible-report lists are bound straight from template expressions (`*ngIf` / `*ngFor`),
 * so Angular re-evaluates them on every change-detection pass. Each evaluation re-ran every
 * report's condition, and those conditions read GrowthBook flags, which re-runs the full
 * targeting rules including `$in` scans over large id lists. Measured on staging that came
 * to roughly 77k array scans per click.
 *
 * Two wins from caching: the conditions stop re-running, and the returned array keeps a
 * stable identity so `NgForOf` stops re-diffing the list every pass.
 *
 * Invalidated on company change or whenever GrowthBook re-evaluates
 * (see {@link GrowthBookService.renderGeneration}).
 */
export class VisibleReportsCache {
  private readonly cache = new Map<string, MenuItem[]>();
  private growthBook: GrowthBookService | null = null;
  private cachedCompany: AuthCompany | null = null;
  private cachedGeneration = -1;

  constructor(private readonly injector: Injector) { }

  get(group: ReportGroup, company: AuthCompany): MenuItem[] {
    this.growthBook ??= this.injector.get(GrowthBookService);
    const generation = this.growthBook.renderGeneration;

    // Compared by reference, not by id: conditions also read company settings, and the
    // store hands out a new object whenever any of them change.
    if (this.cachedCompany !== company || this.cachedGeneration !== generation) {
      this.cache.clear();
      this.cachedCompany = company;
      this.cachedGeneration = generation;
    }

    let visible = this.cache.get(group.key);
    if (!visible) {
      visible = getVisibleReportsForGroup(group, company, this.injector);
      this.cache.set(group.key, visible);
    }
    return visible;
  }
}
